import React from 'react';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

/**
 * RichText Component
 * Renders text with LaTeX support
 * 
 * Usage:
 * - Inline math: $E = mc^2$ or \(E = mc^2\)
 * - Block math: $$\frac{a}{b}$$ or \[\frac{a}{b}\]
 * - Regular text: Just type normally
 * - Mixed: Text with $inline$ math and $$block$$ math
 * 
 * Examples:
 * - Subscript: $K_{sp}$ renders as K subscript sp
 * - Superscript: $10^{-17}$ renders as 10 to the power -17
 * - Fractions: $\frac{a}{b}$ renders as a/b fraction
 * - Chemical: $[Br^-]$ renders as [Br⁻]
 */
const RichText = ({ children, className = '' }) => {
  if (!children || typeof children !== 'string') {
    return <span className={className}>{children}</span>;
  }

  const text = children;
  
  // Split text into parts: regular text, inline math ($...$), and block math ($$...$$)
  const parts = [];
  let currentIndex = 0;
  
  // Regex to match $$...$$ (block) and $...$ (inline) - block first to avoid conflicts
  const mathRegex = /\$\$([^$]+)\$\$|\$([^$]+)\$/g;
  let match;

  while ((match = mathRegex.exec(text)) !== null) {
    // Add text before this match
    if (match.index > currentIndex) {
      parts.push({
        type: 'text',
        content: text.slice(currentIndex, match.index)
      });
    }

    // Check if it's block math ($$...$$) or inline math ($...$)
    if (match[1] !== undefined) {
      // Block math
      parts.push({
        type: 'block',
        content: match[1]
      });
    } else if (match[2] !== undefined) {
      // Inline math
      parts.push({
        type: 'inline',
        content: match[2]
      });
    }

    currentIndex = match.index + match[0].length;
  }

  // Add remaining text after last match
  if (currentIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.slice(currentIndex)
    });
  }

  // If no math found, return plain text
  if (parts.length === 0) {
    return <span className={className}>{text}</span>;
  }

  return (
    <span className={className}>
      {parts.map((part, index) => {
        if (part.type === 'block') {
          return (
            <span key={index} className="block my-2">
              <BlockMath math={part.content} errorColor="#ef4444" />
            </span>
          );
        } else if (part.type === 'inline') {
          return (
            <InlineMath key={index} math={part.content} errorColor="#ef4444" />
          );
        } else {
          return <span key={index}>{part.content}</span>;
        }
      })}
    </span>
  );
};

export default RichText;
